import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { fmtMoney } from '../lib/format';
import type { ProductList } from '../lib/types';
import { Input, Ltr, useDebounced } from './ui';

/** Type letters or a SKU → pick a catalog product. Arrow keys + Enter work. */
export function ProductPicker({ onPick, placeholder = 'חיפוש מוצר לפי שם או מק״ט', autoFocus }: { onPick: (p: { id: number; sku: string; name: string }) => void; placeholder?: string; autoFocus?: boolean }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const dq = useDebounced(q.trim(), 200);
  const box = useRef<HTMLDivElement>(null);
  const { data, isFetching } = useQuery({
    queryKey: ['products', 'picker', dq],
    queryFn: () => api<ProductList>(`/api/products${qs({ q: dq })}`),
    enabled: dq.length >= 2,
    staleTime: 30_000,
  });
  const items = (dq.length >= 2 ? data?.items ?? [] : []).slice(0, 12);
  useEffect(() => setActive(0), [dq]);
  useEffect(() => {
    const close = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const pick = (i: number) => {
    const p = items[i];
    if (!p) return;
    onPick({ id: p.id, sku: p.sku, name: p.description ? `${p.name} — ${p.description}` : p.name });
    setQ('');
    setOpen(false);
  };

  return (
    <div ref={box} className="picker" style={{ position: 'relative' }}>
      <Input
        value={q}
        autoFocus={autoFocus}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(items.length - 1, a + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            pick(active);
          } else if (e.key === 'Escape') setOpen(false);
        }}
      />
      {open && dq.length >= 2 ? (
        <div className="picker-menu" role="listbox">
          {items.length === 0 ? (
            <div className="picker-empty">{isFetching ? 'מחפש…' : 'לא נמצא מוצר'}</div>
          ) : (
            items.map((p, i) => (
              <button
                key={p.id}
                type="button"
                role="option"
                aria-selected={i === active}
                className={`picker-item ${i === active ? 'active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(i);
                }}
              >
                <span className="ellipsis" style={{ flex: 1 }}>
                  {p.name}
                  {p.description ? <span className="muted"> — {p.description}</span> : null}
                </span>
                <Ltr className="small muted tabular">{p.sku}</Ltr>
                <span className="small tabular">{fmtMoney(p.basePrice)}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
